import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QuoteItemDataEditorService } from '../quote-item-data-editor.service';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { DragDropModule } from 'primeng/dragdrop';
import { InputText } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  getQuantityTypeOptions,
  QuantityType,
  QuoteItemData,
  INHERIT_MATERIAL_ID,
} from '../../../../../../models/quote-item-data.model';
import { quantityFieldValidator } from '../../../../../../validators/quantity-field.validator';
import { SelectModule } from 'primeng/select';
import { MaterialSelectComponent } from '../../../../../shared/material-select/material-select.component';
import { InputNumber } from 'primeng/inputnumber';
import { TaxCodeSelectComponent } from '../../../../../tax-code-select/tax-code-select.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { Equipment } from '../../../../../../models/equipment.model';
import {
  Consumable,
  ConsumableSelection,
} from '../../../../../../models/consumable.model';
import { ItemConsumablesEditorComponent } from '../../../../../shared/item-consumables-editor/item-consumables-editor.component';
import { PricingModelService } from '../../../../../services/pricing-model.service';
import { PricingModel } from '../../../../../../models/pricing-model.model';
import { isMissingHourlyRate } from '../../../../../blueprints-viewer/custom-quote-tree';

@Component({
  selector: 'app-quote-item-data-tree',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    Tooltip,
    Popover,
    DragDropModule,
    InputText,
    FormsModule,
    ReactiveFormsModule,
    MaterialSelectComponent,
    SelectModule,
    InputNumber,
    TaxCodeSelectComponent,
    MultiSelectModule,
    ItemConsumablesEditorComponent,
  ],
  templateUrl: './quote-item-data-tree.component.html',
  styleUrl: './quote-item-data-tree.component.scss',
})
/**
 * Component for displaying a single item in the quote item data tree and its children in a tree structure.
 */
export class QuoteItemDataTree implements OnInit, OnChanges, OnDestroy {
  @Input() item!: QuoteItemData;
  @Input() depth: number = 0;
  @Input() currencyCode?: string;
  @Input() equipmentOptions: Equipment[] = [];
  @Input() consumableOptions: Consumable[] = [];
  @Input() consumablesEnabled = false;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Output() addChild = new EventEmitter<string>();
  @Output() removeItem = new EventEmitter<string>();
  @Output() openCadLink = new EventEmitter<QuoteItemData>();
  @Output() relinkCad = new EventEmitter<QuoteItemData>();
  @Output() openPricingParameters = new EventEmitter<QuoteItemData>();

  quoteItemDataEditorService: QuoteItemDataEditorService = inject(
    QuoteItemDataEditorService,
  );
  pricingModelService = inject(PricingModelService);

  fb = new FormBuilder();

  expanded: boolean = false;

  // Form for editing item fields
  form = this.fb.group({
    name: [''],
    quantity: ['', quantityFieldValidator()],
    unit_cost: [''],
    markup_percentage: ['0'],
    quantity_type: [QuantityType.UNIT],
    batch_size: [null as number | null],
    pricing_model_id: [null as number | null],
    material_id: [null as number | null],
    tax_code_id: [null as number | null],
    capex_equipment_ids: [[] as number[]],
  });

  quantityTypeOptions = getQuantityTypeOptions(false, 'short');

  private formSubscription: Subscription | null = null;
  private quantityTypeSubscription: Subscription | null = null;

  dragging$ = this.quoteItemDataEditorService.dragging$;

  ngOnInit(): void {
    if (this.item) {
      this.loadValues();
      this.setupFormSubscription();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      this.loadValues();
      if (!this.formSubscription) {
        this.setupFormSubscription();
      }
    }
  }

  private loadValues(): void {
    if (!this.item) return;

    this.form.patchValue(
      {
        name: this.item.name || '',
        quantity: this.item.quantity || '',
        unit_cost: this.item.unit_cost || '',
        markup_percentage: this.item.markup_percentage ?? '0',
        quantity_type: this.item.quantity_type || QuantityType.UNIT,
        batch_size:
          this.item.batch_size != null && this.item.batch_size > 0
            ? this.item.batch_size
            : null,
        pricing_model_id: this.item.pricing_model_id || null,
        material_id: this.item.inherit_material
          ? INHERIT_MATERIAL_ID
          : this.item.material_id || null,
        tax_code_id: this.item.tax_code_id ?? null,
        capex_equipment_ids: this.item.capex_equipment_ids ?? [],
      },
      { emitEvent: false },
    );

    this.form.markAsPristine({ emitEvent: false });

    this.form.enable({ emitEvent: false });
  }

  private setupFormSubscription(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    if (this.quantityTypeSubscription) {
      this.quantityTypeSubscription.unsubscribe();
    }

    this.quantityTypeSubscription =
      this.form.get('quantity_type')?.valueChanges.subscribe(() => {
        this.form.get('quantity')?.updateValueAndValidity({ emitEvent: false });
      }) ?? null;

    // Subscribe to form changes and update the item via service
    // This ensures the item is updated in the service's items array, maintaining consistency
    this.formSubscription = this.form.valueChanges.subscribe((formValue) => {
      if (!this.item) {
        return;
      }

      // Only update if values actually changed to prevent infinite loops
      const name = formValue.name || '';
      const quantity = formValue.quantity || undefined;
      const unit_cost = formValue.unit_cost || undefined;
      const markup_percentage =
        formValue.markup_percentage === null ||
        formValue.markup_percentage === undefined
          ? undefined
          : formValue.markup_percentage;
      const quantity_type =
        formValue.quantity_type === null ||
        formValue.quantity_type === undefined
          ? undefined
          : (formValue.quantity_type as QuantityType);
      const batch_size =
        formValue.batch_size != null && formValue.batch_size > 0
          ? formValue.batch_size
          : null;
      const pricing_model_id =
        formValue.pricing_model_id === null ||
        formValue.pricing_model_id === undefined
          ? undefined
          : formValue.pricing_model_id;
      const inherit_material = formValue.material_id === INHERIT_MATERIAL_ID;
      const material_id = inherit_material
        ? null
        : formValue.material_id || null;
      const tax_code_id = formValue.tax_code_id ?? null;
      const capex_equipment_ids = formValue.capex_equipment_ids ?? [];
      const itemBatchSize =
        this.item.batch_size != null && this.item.batch_size > 0
          ? this.item.batch_size
          : null;

      // Check if any value actually changed
      if (
        name === this.item.name &&
        quantity === this.item.quantity &&
        unit_cost === this.item.unit_cost &&
        markup_percentage === this.item.markup_percentage &&
        quantity_type === this.item.quantity_type &&
        batch_size === itemBatchSize &&
        pricing_model_id === this.item.pricing_model_id &&
        material_id === this.item.material_id &&
        inherit_material === (this.item.inherit_material ?? false) &&
        tax_code_id === this.item.tax_code_id &&
        this.areNumberArraysEqual(
          capex_equipment_ids,
          this.item.capex_equipment_ids ?? [],
        )
      ) {
        return; // No changes, skip update
      }

      // Update the item via service to maintain consistency with the items array
      this.quoteItemDataEditorService.updateItem(this.item, {
        name,
        quantity,
        unit_cost,
        markup_percentage,
        quantity_type,
        batch_size,
        pricing_model_id,
        material_id,
        inherit_material,
        tax_code_id,
        capex_equipment_ids,
      });
    });
  }

  get children(): QuoteItemData[] {
    const serviceItems = this.quoteItemDataEditorService.items();
    const actualChildren = serviceItems.filter(
      (item) => item.parent_id === this.item.persistent_id,
    );

    if (actualChildren.length > 0) {
      return this.quoteItemDataEditorService.sortSiblingsBySiblingOrder(
        actualChildren,
      );
    }

    return [];
  }

  onToggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  onConsumablesChange(consumables: ConsumableSelection[]): void {
    this.quoteItemDataEditorService.updateItem(this.item, { consumables });
  }

  /**
   * Returns true if manual pricing mode is selected (no pricing model)
   */
  get isManualPricing(): boolean {
    const pricingModelId = this.form.get('pricing_model_id')?.value;
    return pricingModelId === null || pricingModelId === undefined;
  }

  /**
   * Returns true if the selected pricing model has output_quantity enabled
   * When output_quantity is enabled:
   * - Quantity is calculated by the pricing model (read-only)
   * - Unit cost is manually set by the user
   */
  get selectedPricingModelHasOutputQuantity(): boolean {
    return this.selectedPricingModel?.output_quantity ?? false;
  }

  get selectedPricingModelHasOutputUnitCost(): boolean {
    return this.selectedPricingModel?.output_unit_cost ?? false;
  }

  /**
   * Hours-only pricing model with no configured rate yet.
   */
  get needsHourlyRateInput(): boolean {
    if (this.children.length > 0) {
      return false;
    }
    if (this.isManualPricing || this.selectedPricingModelHasOutputUnitCost) {
      return false;
    }
    return isMissingHourlyRate(this.form.get('unit_cost')?.value);
  }

  get unmatchedMaterialSuggestion(): string | null {
    const name = this.item.suggested_material_name?.trim();
    if (!name) {
      return null;
    }
    if (this.item.inherit_material || this.item.material_id != null) {
      return null;
    }
    return `AI suggested material: ${name}`;
  }

  get unmatchedPricingModelSuggestion(): string | null {
    const name = this.item.suggested_pricing_model_name?.trim();
    if (!name) {
      return null;
    }
    if (this.item.pricing_model_id != null) {
      return null;
    }
    return `AI suggested pricing: ${name}`;
  }

  get selectedPricingModel(): PricingModel | null {
    // FixMe: Not ideal to be searching through pricing models every time, but copying implementation from above for now
    const pricingModelId = this.form.get('pricing_model_id')?.value;
    if (pricingModelId === null || pricingModelId === undefined) {
      return null;
    }
    return this.pricingModelService.pricingModelsById()[pricingModelId];
  }

  /** True when the item has no tax code yet (use default); false when it has one (e.g. from blueprint, keep it). */
  get isNewItem(): boolean {
    return this.item.tax_code_id == null;
  }

  get cadColumnIcon(): string {
    if (this.item.drawing_file_id) {
      return 'pi pi-file-pdf';
    }
    return this.item.cad_object_ids.length > 0
      ? 'pi pi-file-check'
      : 'pi pi-upload';
  }

  /** Badge count for linked CAD objects or drawing PDF */
  get linkedAssetBadge(): string | undefined {
    if (this.item.drawing_file_id) {
      return '1';
    }
    if (this.item.cad_object_ids.length > 0) {
      return this.item.cad_object_ids.length.toString();
    }
    return undefined;
  }

  get cadLinkTooltip(): string {
    if (this.item.drawing_file_id) {
      const filename = this.item.drawing_file_name?.trim() || 'drawing PDF';
      return `Linked to drawing PDF: ${filename}`;
    }
    const names = this.item.cad_object_names ?? [];
    if (names.length === 0) {
      return 'Click to link CAD objects to this item.';
    }
    if (names.length === 1) {
      return `Linked to '${names[0]}'`;
    }
    return `Linked to ${names.length} CAD objects: ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  get capexTooltip(): string {
    const ids = this.item.capex_equipment_ids ?? [];
    if (ids.length === 0) {
      return 'Select CapEx equipment for this item.';
    }

    const names = this.equipmentOptions
      .filter((equipment) => ids.includes(equipment.id))
      .map((equipment) => equipment.name);
    return names.length > 0
      ? `Selected CapEx equipment: ${names.join(', ')}`
      : `${ids.length} CapEx equipment item${ids.length === 1 ? '' : 's'} selected`;
  }

  private areNumberArraysEqual(first: number[], second: number[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSorted = [...first].sort((a, b) => a - b);
    const secondSorted = [...second].sort((a, b) => a - b);
    return firstSorted.every((value, index) => value === secondSorted[index]);
  }

  onAddChildToThisItem(parentId: string): void {
    this.addChild.emit(parentId);
  }

  onRemoveItem(): void {
    this.removeItem.emit(this.item.persistent_id);
  }

  onOpenPricingParameters(): void {
    const latestItem =
      this.quoteItemDataEditorService
        .items()
        .find(
          (candidate) => candidate.persistent_id === this.item.persistent_id,
        ) ?? this.item;

    this.openPricingParameters.emit({
      ...latestItem,
      pricing_model_id:
        this.selectedPricingModel?.id ?? latestItem.pricing_model_id,
    });
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.quantityTypeSubscription) {
      this.quantityTypeSubscription.unsubscribe();
    }
  }

  protected readonly QuantityType = QuantityType;
}
